import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Lock, Check } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

function arraysEqual(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
}

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

const CorrectOrderForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const items = content?.items || [];
    const initial = userAnswer?.order && Array.isArray(userAnswer.order) && userAnswer.order.length === items.length
        ? [...userAnswer.order] : (items.length ? shuffle([...items]) : []);
    const [ranking, setRanking] = useState(initial);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const lastItemsKey = useRef('');
    // Guards against a socket-reconnect resync (JoinPage re-fetches the active activity and
    // calls setUserAnswer with a freshly-parsed object on every reconnect, even when nothing
    // actually changed) silently clobbering a reorder the participant is mid-way through but
    // hasn't submitted yet. Flips true the moment a drag/move happens since the last submit,
    // and resets on a successful submit or when userAnswer goes back to null (a genuinely new
    // activity, where there's nothing local worth preserving).
    const hasLocalEditsRef = useRef(false);

    useEffect(() => {
        if (userAnswer == null) {
            hasLocalEditsRef.current = false;
        }
        if (hasLocalEditsRef.current) return;
        if (userAnswer?.order && Array.isArray(userAnswer.order) && userAnswer.order.length === items.length) {
            setRanking([...userAnswer.order]);
            return;
        }
        if (userAnswer == null && items.length) {
            const key = items.length + '-' + items.join('|');
            if (lastItemsKey.current !== key) {
                lastItemsKey.current = key;
                setRanking(shuffle([...items]));
            }
        }
    }, [userAnswer, items]);

    const handleDragStart = (e, index) => {
        if (optionsDisabled) return;
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.setData('application/json', JSON.stringify({ index }));
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragIndex !== null && dragIndex !== index) setDragOverIndex(index);
    };

    const handleDragLeave = () => setDragOverIndex(null);

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        setDragOverIndex(null);
        if (dragIndex == null || isLocked || dragIndex === dropIndex) {
            setDragIndex(null);
            return;
        }
        const next = [...ranking];
        const [removed] = next.splice(dragIndex, 1);
        next.splice(dropIndex, 0, removed);
        hasLocalEditsRef.current = true;
        setRanking(next);
        setDragIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    // Touch/keyboard fallback: native HTML5 drag-and-drop never fires on mobile
    // touch devices, so moving an item up/down via tap or Enter/Space swaps it
    // with its neighbor in the same `ranking` array the drag handlers mutate.
    const moveItem = (index, direction) => {
        if (optionsDisabled) return;
        const target = index + direction;
        if (target < 0 || target >= ranking.length) return;
        const next = [...ranking];
        [next[index], next[target]] = [next[target], next[index]];
        hasLocalEditsRef.current = true;
        setRanking(next);
    };

    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && arraysEqual(ranking, userAnswer?.order || []);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit order')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content?.question || 'Arrange in the correct order'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>
            {content?.settings?.description && content?.settings?.descText && (
                <div className={styles.description}>{content.settings.descText}</div>
            )}
            {content?.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question context" />
            )}
            <span className={styles.hint}>
                Items are shown in random order. Drag and drop to arrange them in the correct sequence (top to bottom), then tap Submit order.
            </span>
            <div className={styles.correctOrderList} role="list">
                {ranking.map((item, i) => (
                    <div
                        key={i}
                        role="listitem"
                        aria-label={item ? `${i + 1}. ${item}` : `Item ${i + 1}`}
                        aria-disabled={optionsDisabled}
                        className={`${styles.correctOrderItem} ${dragIndex === i ? styles.correctOrderItemDragging : ''} ${dragOverIndex === i ? styles.correctOrderItemDragOver : ''}`}
                        draggable={!optionsDisabled}
                        onDragStart={(e) => handleDragStart(e, i)}
                        onDragOver={(e) => handleDragOver(e, i)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, i)}
                        onDragEnd={handleDragEnd}
                    >
                        <span className={styles.correctOrderGrip} title="Drag to reorder" aria-label="Drag to reorder">
                            <ArrowUpDown size={18} />
                        </span>
                        <span className={styles.correctOrderMoveGroup}>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => moveItem(i, -1)}
                                disabled={optionsDisabled || i === 0}
                                aria-label="Move up"
                            >
                                <ArrowUp size={16} />
                            </button>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => moveItem(i, 1)}
                                disabled={optionsDisabled || i === ranking.length - 1}
                                aria-label="Move down"
                            >
                                <ArrowDown size={16} />
                            </button>
                        </span>
                        <span className={styles.correctOrderNumber}>{i + 1}</span>
                        <span className={styles.correctOrderLabel}>{item || `Item ${i + 1}`}</span>
                    </div>
                ))}
            </div>
            <button
                className={styles.submitBtn}
                onClick={() => {
                    if (optionsDisabled) return;
                    onSubmit({ order: ranking });
                    hasLocalEditsRef.current = false;
                }}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default CorrectOrderForm;
